import { ColorEnum } from '@/constants/color'
import { PaymentDifference } from '@/features/shiftCloseouts/types'
import CheckmarkIcon from '@/features/ui/icons/components/CheckmarkIcon'
import Typography from '@/features/ui/typography'
import { formatPrice } from '@/utils/priceHelper'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleProp, View, ViewStyle } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface DifferenceBadgeProps {
    difference?: PaymentDifference
    paymentMethodLabel: string
    placeholder?: boolean
    style?: StyleProp<ViewStyle>
}

const DifferenceBadge: FC<DifferenceBadgeProps> = ({
    difference,
    paymentMethodLabel,
    placeholder = false,
    style,
}) => {
    const { t } = useTranslation()
    const { styles } = useStyles(stylesheet, {
        state: placeholder ? 'incomplete' : (difference?.state ?? 'incomplete'),
    })
    const formattedDifference = difference
        ? formatPrice(
              difference.amount.amount,
              difference.amount.currency,
              difference.state === 'surplus'
          )
        : undefined
    const accessibilityLabel = placeholder
        ? undefined
        : difference
          ? difference.state === 'balanced'
              ? t('shiftCloseouts.paymentMethods.balancedAmount', {
                    paymentMethod: paymentMethodLabel,
                })
              : t('shiftCloseouts.paymentMethods.differenceAmount', {
                    amount: formattedDifference,
                    paymentMethod: paymentMethodLabel,
                })
          : t('shiftCloseouts.paymentMethods.missingAmount', {
                paymentMethod: paymentMethodLabel,
            })

    return (
        <View
            accessibilityLabel={accessibilityLabel}
            accessibilityElementsHidden={placeholder}
            accessible={!placeholder}
            importantForAccessibility={placeholder ? 'no-hide-descendants' : 'auto'}
            style={[styles.container, style]}
            testID="shift-closeout-payment-difference"
        >
            {placeholder ? (
                <Typography.Description font="medium" style={styles.text}>
                    —
                </Typography.Description>
            ) : difference?.state === 'balanced' ? (
                <CheckmarkIcon fill={ColorEnum.Emerald600} height={16} width={16} />
            ) : difference ? (
                <Typography.Description font="medium" style={styles.text}>
                    {formattedDifference}
                </Typography.Description>
            ) : null}
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        width: 120,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 12,
        borderRadius: 8,
        variants: {
            state: {
                balanced: { backgroundColor: ColorEnum.EMERALD_100 },
                incomplete: { backgroundColor: ColorEnum.VIOLET_100 },
                shortage: { backgroundColor: ColorEnum.Brick100 },
                surplus: { backgroundColor: ColorEnum.Yellow100 },
            },
        },
    },
    text: {
        variants: {
            state: {
                balanced: { color: ColorEnum.Emerald600 },
                incomplete: { color: ColorEnum.GRAY_600 },
                shortage: { color: ColorEnum.BRICK_500 },
                surplus: { color: ColorEnum.YELLOW_600 },
            },
        },
    },
})

export default DifferenceBadge
